/**
 * AtelierPage — l'écran unique de la refonte « Encre & Papier ».
 *
 * Fusion du générateur et de la composition : à gauche on choisit et on
 * génère, à droite LA FICHE RÉELLE se construit (vraies pages A4, bascule
 * Fiche élève / Corrigé), avec téléchargement et sauvegarde sur place.
 *
 * Parité fonctionnelle avec l'ancien couple générateur + composer :
 * catalogue par classe, recherche, génération par lot, ajout/retrait/
 * réordonnancement, changer les valeurs (blueprint épinglé), titre,
 * corrigé + niveau de détail, export PDF (gate compte + quota 429),
 * sauvegarde dans Mes fiches, restauration de la sélection (localStorage).
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../hooks/useAuth";
import BugReportDialog from "./admin/BugReportDialog";
import SignalerLaPage from "./SignalerLaPage";
import { useLogin } from "../contexts/LoginContext";
import { useSelection } from "../contexts/SelectionContext";
import { useExportPdfGate } from "../lib/exportPdfUtils";
import MathHtmlRenderer from "../lib/MathHtmlRenderer";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { chercherChapitres } from "../lib/rechercheNotion";
import OptionsExportPDF, { VALEURS_INITIALES } from "./export/OptionsExportPDF";
import SheetPreview from "./atelier/SheetPreview";
import ApercuExercice from "./atelier/ApercuExercice";
import { useBeta } from "../hooks/useBeta";
// Le mécanisme d'intention-qui-survit-à-la-connexion vit désormais dans un
// module partagé : le parcours H en a besoin lui aussi, et il ne doit pas
// exister en deux exemplaires qui finiraient par diverger.
import {
  noterActionApresConnexion, reprendreActionApresConnexion,
} from "../lib/actionApresConnexion";
import {
  Loader2, Plus, Check, RefreshCw, ChevronDown, ChevronUp, Download, Save, X, Search, Eye,
} from "lucide-react";
import { copieInattendue, decisionDEnregistrement } from "./fiche/enregistrement";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LEVELS_ORDER = ["CP", "CE1", "CE2", "CM1", "CM2", "6e", "5e", "4e", "3e"];

/* LA REPRISE D'ÉTAT NE GARDAIT QUE LES EXERCICES.
 *
 * `SelectionContext` persiste la sélection ; le niveau et le chapitre, eux,
 * vivaient dans l'état local et mouraient avec lui. Après un rechargement,
 * l'utilisateur retrouvait donc sa fiche mais devant un formulaire vide et un
 * bouton « Générer » gris : la fiche est là, on ne peut plus la compléter.
 *
 * On garde donc les deux. À part de la sélection, parce que ce n'est pas la
 * même chose : la sélection est le TRAVAIL, ceci n'est que l'endroit où on
 * l'a laissé. Et le niveau est validé à la lecture — un intitulé retiré du
 * référentiel ne doit pas rendre l'écran incohérent.
 *
 * S'y ajoutent LE TITRE, et la trace du dernier enregistrement.
 *
 * Le titre disparaissait au moindre rechargement alors que les exercices
 * restaient : on nommait sa fiche, on revenait, le nom n'était plus là. Pire,
 * il disparaissait aussi en passant par la connexion — donc au moment précis
 * où l'on cherchait à enregistrer, et la fiche partait sous son nom par défaut.
 *
 * La trace d'enregistrement — l'identifiant de la fiche et l'empreinte de ce
 * qui a été envoyé — sert à dire la vérité à l'écran : cette fiche est-elle
 * dans Mes fiches, et telle qu'on la voit ? Sans elle, l'utilisateur ne
 * pouvait pas savoir si ce qu'il regardait existait ailleurs que dans son
 * navigateur. */
const CLE_ATELIER = "lemaitremot_atelier";

/* Ce qui définit « la même fiche » : son titre et l'ordre de ses exercices.
 * Le contenu d'un exercice peut changer sous lui — « Changer les valeurs » le
 * remplace en place — et cela COMPTE comme une modification, d'où l'empreinte
 * du contenu et pas seulement des identifiants. */
const empreinteFiche = (titre, exercices) => JSON.stringify([
  titre || "",
  (exercices || []).map((e) => `${e.uniqueId}:${(e.enonce_html || "").length}`),
]);

const lireAtelier = () => {
  try {
    const v = JSON.parse(localStorage.getItem(CLE_ATELIER) || "null");
    if (!v || typeof v !== "object") return null;
    return {
      niveau: LEVELS_ORDER.includes(v.niveau) ? v.niveau : null,
      chapitre: typeof v.chapitre === "string" ? v.chapitre : "",
      titre: typeof v.titre === "string" ? v.titre : "",
      ficheUid: typeof v.ficheUid === "string" ? v.ficheUid : null,
      empreinte: typeof v.empreinte === "string" ? v.empreinte : null,
    };
  } catch (e) {
    return null;   // navigation privée, stockage plein, JSON abîmé : on continue sans
  }
};

const normalize = (s) =>
  (s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

// Un intitulé de proposition n'est montré que s'il parle à un enseignant :
// les libellés techniques de blueprints (« Liens : a/b ↔ c », « (cahier +
// a/b/c) », underscores…) sont remplacés par « Proposition N ».
const cleanLabel = (label) => {
  if (!label) return null;
  const l = String(label).trim();
  if (l.length < 4 || l.length > 48) return null;
  if (/[↔_()]|\.\.\.|\//.test(l)) return null;
  return l;
};

/*
 * LE TITRE DE L'EXERCICE, QUAND LA VARIANTE N'EN DONNE PAS.
 *
 * L'en-tête d'une proposition lisait `blueprint_label` — l'étiquette de la
 * VARIANTE — et se rabattait sur « Proposition N » quand elle manquait. Or un
 * générateur n'est pas tenu de déclarer des variantes : tous ceux qui n'en ont
 * pas devenaient anonymes dans la liste, y compris quand leur exercice porte
 * un titre parfaitement clair.
 *
 * Constaté sur un générateur fraîchement publié : ses trois exercices
 * s'affichaient « Proposition 2, 3, 4 » alors qu'ils s'intitulaient
 * « Automatismes : calculs avec des décimaux ». On les cherchait par leur nom,
 * et c'était leur nom qui manquait.
 *
 * On lit donc le titre rendu, que les deux familles de gabarits portent au
 * même endroit — la cellule `lmm-titre` de l'en-tête. Sans inventer de
 * variante dans le générateur pour obtenir une étiquette : le titre existe
 * déjà, il suffit de le regarder.
 */
/* « frac_probleme » -> « Frac probleme ». Le dernier recours AVANT le rang.
 *
 * Mesuré en production : deux propositions sur cinq s'annonçaient « Proposition
 * 2 » et « Proposition 5 » — le générateur ne nommait pas sa variante et son
 * gabarit ne portait pas de titre. « Proposition 5 » ne dit rien de ce qu'on
 * regarde ; l'identifiant du type, lui, en dit déjà l'essentiel. */
const titreDeLaVariante = (identifiant) => {
  const mots = String(identifiant || "").replace(/[_-]+/g, " ").trim();
  if (mots.length < 3) return null;
  return mots.charAt(0).toUpperCase() + mots.slice(1);
};

const titreDeLExercice = (html) => {
  if (!html) return null;
  const m = String(html).match(
    /class\s*=\s*"[^"]*\blmm-titre\b[^"]*"[^>]*>([\s\S]*?)<\/t[dh]>/i
  );
  if (!m) return null;
  const texte = m[1].replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  return texte.length >= 4 && texte.length <= 60 ? texte : null;
};

/* DEUX RENDUS DU MÊME EXERCICE NE SONT JAMAIS IDENTIQUES OCTET POUR OCTET.
 *
 * Les figures portent des identifiants générés à chaque rendu — « line_tr_27269 »,
 * repris dans la feuille de style embarquée. Une comparaison brute répondrait
 * donc « différent » à tous les coups, y compris sur un exercice qui n'a
 * qu'une forme. On neutralise ces identifiants, et RIEN D'AUTRE : les
 * coordonnées et les valeurs de la figure restent comparées, parce que sept
 * des générateurs mesurés ne font varier leurs données QUE dans le dessin.
 */
const IDENTIFIANTS_GENERES = /\b[A-Za-z][A-Za-z0-9]*_[A-Za-z0-9]+_\d{3,}\b/g;

const empreinteExercice = (ex) =>
  `${ex?.enonce_html || ""} ${ex?.solution_html || ""}`
    .replace(IDENTIFIANTS_GENERES, "ID");

const textSnippet = (html, len = 90) => {
  const t = (html || "")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return t.length > len ? t.slice(0, len) + "…" : t;
};

/**
 * Le corps d'une proposition — assez haut pour juger, pas assez pour noyer.
 *
 * Sans borne, un exercice long poussait les propositions suivantes hors de
 * l'écran : « le contenu descend très loin et oblige à scroller énormément ».
 * Avec une borne fixe pour tous, on tronquait justement l'exercice qu'on
 * demande de juger.
 *
 * On mesure donc, et on ne borne QUE ce qui dépasse : les exercices courts —
 * la majorité — ne sont pas touchés, les longs annoncent qu'ils continuent et
 * s'ouvrent d'un clic. Aucune barre de défilement interne : elle capturait
 * celle de la page, et quinze crans de molette ne la bougeaient pas d'un pixel.
 */
const HAUTEUR_JUGEABLE = 380;

function CorpsProposition({ children, deplie, onDeplier }) {
  const zone = useRef(null);
  const [deborde, setDeborde] = useState(false);

  useLayoutEffect(() => {
    const el = zone.current;
    if (!el) return undefined;
    const mesurer = () => setDeborde(el.scrollHeight > HAUTEUR_JUGEABLE + 24);
    mesurer();
    const ro = new ResizeObserver(mesurer);
    ro.observe(el);
    return () => ro.disconnect();
  }, [children]);

  const borne = deborde && !deplie;
  return (
    <div className="relative">
      <div ref={zone}
        style={borne ? { maxHeight: HAUTEUR_JUGEABLE, overflow: "hidden" } : undefined}>
        {/* Un tableau large défile DANS sa boîte plutôt que de pousser la page.
            Mesuré : 2 tableaux sur 1 025 dépassent huit colonnes — rare, mais
            sur téléphone ces deux-là faisaient défiler l'atelier entier. */}
        <div className="max-w-full overflow-x-auto">{children}</div>
      </div>
      {borne && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-card to-transparent" />
      )}
      {deborde && (
        <button type="button"
          className="relative z-10 mt-1 text-[13px] font-medium text-primary underline underline-offset-2"
          onClick={onDeplier}>
          {deplie ? "Réduire" : "Voir l'exercice en entier"}
        </button>
      )}
    </div>
  );
}

export default function AtelierPage() {
  const navigate = useNavigate();
  const beta = useBeta();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { userEmail, isPro, sessionToken, isLoading: authLoading,
          peutSignaler } = useAuth();
  //: L'exercice sur lequel un signalement est en cours, et rien d'autre.
  const [exerciceSignale, setExerciceSignale] = useState(null);
  const { openLogin } = useLogin();
  const { checkBeforeExport, handleExportError } = useExportPdfGate();
  const {
    selectedExercises, addExercise, removeExercise, replaceExercise,
    reorderExercises, isSelected, clearSelection, restoreExercise,
    importSelectionToAccount,
  } = useSelection();

  /* ------------------------- catalogue ------------------------- */
  const [levels, setLevels] = useState(LEVELS_ORDER);
  //: Lu une seule fois, au montage. L'URL prime : `?classe=` est une intention
  //: explicite, la mémoire n'est qu'un défaut.
  const [memoire] = useState(lireAtelier);
  const [niveau, setNiveau] = useState(
    () => searchParams.get("classe") || memoire?.niveau || "6e");
  const [catalog, setCatalog] = useState(null);           // {domains:[{name, chapters:[...]}]}
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState(null);
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [chapitre, setChapitre] = useState("");           // code_officiel
  const autoSelectDone = useRef(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/v1/levels`).then((r) => {
      const codes = (r.data?.levels || r.data || []).map((l) => l.code || l);
      if (codes.length) {
        setLevels(LEVELS_ORDER.filter((c) => codes.includes(c)));
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    let dead = false;
    setCatalogLoading(true);
    setCatalogError(null);
    axios.get(`${BACKEND_URL}/api/v1/curriculum/${niveau}/catalog`)
      .then((r) => { if (!dead) setCatalog(r.data); })
      .catch(() => { if (!dead) setCatalogError("Impossible de charger les chapitres. Vérifiez votre connexion puis réessayez."); })
      .finally(() => { if (!dead) setCatalogLoading(false); });
    return () => { dead = true; };
  }, [niveau]);

  const allChapters = useMemo(() => {
    if (!catalog?.domains) return [];
    return catalog.domains.flatMap((d) =>
      (d.chapters || []).map((c) => ({ ...c, domaine: d.name })));
  }, [catalog]);

  // La recherche vit dans `lib/rechercheNotion` : racines pour les variantes
  // naturelles, table d'équivalences écrite à la main pour les relations
  // pédagogiques. « division » y trouve enfin « diviseurs ».
  const results = useMemo(
    () => chercherChapitres(allChapters, query), [query, allChapters]);

  // Arrivée depuis l'accueil avec ?q= : si un seul chapitre correspond, le choisir
  useEffect(() => {
    if (autoSelectDone.current || !results || catalogLoading) return;
    if (searchParams.get("q") && results.length === 1) {
      setChapitre(results[0].code_officiel);
      autoSelectDone.current = true;
    }
  }, [results, catalogLoading, searchParams]);

  /* Le chapitre mémorisé se repose APRÈS le catalogue, et une seule fois.
   *
   * Après, parce qu'un code officiel qui n'est pas encore dans la liste ne
   * sélectionnerait rien ; on vérifie donc qu'il y figure toujours, ce qui
   * couvre au passage un chapitre retiré du référentiel.
   *
   * Une seule fois, parce que changer de classe remet volontairement le
   * chapitre à zéro : sans ce verrou, l'effet le ressusciterait derrière
   * l'utilisateur au rechargement du catalogue suivant.
   *
   * Et jamais contre une intention explicite : `?q=` vient de l'accueil et
   * désigne ce que l'utilisateur cherche MAINTENANT. */
  const repriseFaite = useRef(false);
  useEffect(() => {
    if (repriseFaite.current || catalogLoading) return;
    const vise = memoire?.chapitre;
    if (vise && !searchParams.get("q") && memoire.niveau === niveau
        && allChapters.some((c) => c.code_officiel === vise)) {
      setChapitre(vise);
    }
    repriseFaite.current = true;
  }, [catalogLoading, allChapters, memoire, niveau, searchParams]);

  /**
   * Quand la classe courante ne répond rien, on regarde les autres.
   *
   * « Rien en 6e. Essayez un autre mot, ou changez de classe » disait à
   * l'utilisateur de chercher lui-même, sans dire où. C'est précisément le
   * parent qui ne sait pas à quel niveau se range une notion. On ne charge les
   * autres catalogues QUE dans ce cas, et une seule fois par niveau.
   */
  //: Le champ et sa liste, pour savoir ce qui est « à l'extérieur ».
  const zoneRecherche = useRef(null);

  // La liste de résultats ne se fermait jamais : ni au clic ailleurs, ni à
  // Échap. Elle restait posée sur la page, y compris sur téléphone où elle
  // recouvre le reste du panneau.
  useEffect(() => {
    if (!query) return undefined;
    const dehors = (e) => {
      if (zoneRecherche.current && !zoneRecherche.current.contains(e.target)) setQuery("");
    };
    const echap = (e) => { if (e.key === "Escape") setQuery(""); };
    document.addEventListener("pointerdown", dehors);
    document.addEventListener("keydown", echap);
    return () => {
      document.removeEventListener("pointerdown", dehors);
      document.removeEventListener("keydown", echap);
    };
  }, [query]);

  const cataloguesAutresNiveaux = useRef({});
  const [ailleurs, setAilleurs] = useState([]);

  useEffect(() => {
    const rienIci = results && results.length === 0 && query.trim().length >= 3;
    if (!rienIci) { setAilleurs([]); return; }
    let mort = false;
    const autres = levels.filter((n) => n !== niveau);
    Promise.all(autres.map(async (n) => {
      if (!cataloguesAutresNiveaux.current[n]) {
        try {
          const r = await axios.get(`${BACKEND_URL}/api/v1/curriculum/${n}/catalog`);
          cataloguesAutresNiveaux.current[n] = (r.data?.domains || [])
            .flatMap((d) => (d.chapters || []).map((c) => ({ ...c, domaine: d.name })));
        } catch (e) { cataloguesAutresNiveaux.current[n] = []; }
      }
      return (chercherChapitres(cataloguesAutresNiveaux.current[n], query) || [])
        .slice(0, 2).map((c) => ({ ...c, niveau: n }));
    })).then((paquets) => {
      if (!mort) setAilleurs(paquets.flat().slice(0, 4));
    });
    return () => { mort = true; };
  }, [results, query, levels, niveau]);

  const currentChapter = allChapters.find((c) => c.code_officiel === chapitre) || null;

  /* ------------------------- génération ------------------------- */
  const [batchSize, setBatchSize] = useState(5);
  const [difficulte, setDifficulte] = useState("moyen");
  const [proposals, setProposals] = useState([]);
  const [generating, setGenerating] = useState(false);

  /* LA VARIÉTÉ RÉELLEMENT DISPONIBLE.
   *
   * « Si je demande 5 exercices alors qu'un générateur possède beaucoup plus de
   * formes intéressantes, je peux ne pas revoir facilement un exercice que
   * j'avais aimé. » Rien à l'écran ne disait combien de formes existent : cinq
   * propositions pouvaient être cinq sur cinq, ou cinq sur cinquante.
   *
   * Le chapitre le sait ; il suffisait de le demander. */
  const [varietes, setVarietes] = useState(null);
  const [varietesOuvertes, setVarietesOuvertes] = useState(false);
  //: L'emplacement en cours de remplacement, pour n'animer que celui-là.
  const [remplaceId, setRemplaceId] = useState(null);
  //: L'exercice ouvert en grand.
  const [apercu, setApercu] = useState(null);
  //: Les propositions dépliées, quand leur hauteur a été bornée.
  const [depliees, setDepliees] = useState({});
  /* LA GRAINE QU'ON DEMANDE À REVOIR — ET DE QUOI LA REPRODUIRE À L'IDENTIQUE.
   *
   * Une graine seule ne suffit pas toujours. Mesuré sur le parc : chez 251
   * générateurs sur 339, le choix du type d'exercice puise dans le même flux
   * aléatoire que l'exercice ; imposer le type décale donc tout le reste. La
   * graine reste déterministe pour un chapitre et une difficulté donnés, mais
   * pour retrouver EXACTEMENT la carte qu'on avait sous les yeux, il faut
   * aussi sa série et sa forme. On les retient quand la graine vient d'un clic
   * sur une carte ; un nombre tapé à la main n'a que lui-même. */
  const [graineVoulue, setGraineVoulue] = useState("");
  const [origineDeLaGraine, setOrigineDeLaGraine] = useState(null);
  //: Le bloc de propositions, pour pouvoir l'amener sous les yeux.
  const blocPropositions = useRef(null);
  //: Ce que les lecteurs d'écran doivent entendre après une génération.
  const [annonce, setAnnonce] = useState("");
  //: L'exercice qu'on vient d'ajouter, le temps d'un « Ajouté ✓ ».
  const [ajoutRecent, setAjoutRecent] = useState(null);
  const [genError, setGenError] = useState(null);
  const [openSolutions, setOpenSolutions] = useState({});

  /**
   * Amène le bloc de propositions sous les yeux — et SEULEMENT si besoin.
   *
   * Mesuré avant correctif, fenêtre de 723 px : la première proposition
   * naissait à 870 px, donc hors de l'écran, sans un mot. Mais arracher
   * quelqu'un à sa position quand le bloc est déjà là serait pire que le mal :
   * on ne défile que s'il en reste moins de `SEUIL_VISIBLE` à l'écran.
   *
   * `behavior: "smooth"` est abandonné si le système demande moins
   * d'animations — un défilement animé non désiré donne le mal de mer.
   */
  const montrerLesPropositions = () => {
    const SEUIL_VISIBLE = 180;
    requestAnimationFrame(() => {
      const bloc = blocPropositions.current;
      if (!bloc) return;
      const r = bloc.getBoundingClientRect();
      const visible = Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0);
      // Deux conditions, et la seconde compte surtout sur téléphone : à 390 px
      // le bloc commençait à 597 px pour un écran de 844, donc 247 px
      // « visibles » — mais c'était l'en-tête de section, pas une seule
      // proposition. On défile aussi dès que le bloc démarre sous la moitié
      // de l'écran.
      const demarre_bas = r.top > window.innerHeight * 0.5;
      if (visible >= SEUIL_VISIBLE && !demarre_bas) return;
      const doux = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const cible = window.scrollY + r.top - 72;   // sous la barre de navigation
      window.scrollTo({ top: Math.max(0, cible), behavior: doux ? "smooth" : "auto" });
    });
  };

  useEffect(() => {
    if (!chapitre) { setVarietes(null); return undefined; }
    let mort = false;
    axios
      .get(`${BACKEND_URL}/api/v1/curriculum/chapitres/${chapitre}/varietes`, {
        params: { difficulte },
      })
      .then((r) => { if (!mort) setVarietes(r.data); })
      .catch(() => { if (!mort) setVarietes(null); });
    return () => { mort = true; };
  }, [chapitre, difficulte]);

  /**
   * Une proposition de plus, d'une forme précise — ou pour un seul emplacement.
   *
   * Le seul geste possible jusqu'ici était « Générer 5 exercices », qui
   * remplaçait tout. Pour changer celui du milieu, il fallait relancer les cinq
   * et espérer. On demande donc UN exercice, en nommant la série et le type
   * quand on les connaît, et on ne touche qu'à la carte visée.
   */
  /** Une forme du chapitre qu'aucune proposition affichée ne porte déjà. */
  const formeInedite = () => {
    const series = varietes?.series || [];
    if (!series.length) return null;
    const dejaLa = new Set(
      proposals.map((e) => `${e.metadata?.generator_key}::${e.metadata?.blueprint_id_used || ""}`)
    );
    const toutes = [];
    series.forEach((serie) => {
      if (serie.variantes.length === 0) toutes.push({ generatorKey: serie.generator_key, blueprintId: null });
      serie.variantes.forEach((v) => toutes.push({ generatorKey: serie.generator_key, blueprintId: v.id }));
    });
    const inedites = toutes.filter(
      (f) => !dejaLa.has(`${f.generatorKey}::${f.blueprintId || ""}`)
    );
    if (!inedites.length) return null;
    return inedites[Math.floor(Math.random() * inedites.length)];
  };

  const proposerUn = async ({ generatorKey, blueprintId, remplaceUid, graine } = {}) => {
    if (!chapitre) return;
    if (remplaceUid) setRemplaceId(remplaceUid);
    // REMPLACER, C'EST PROPOSER AUTRE CHOSE.
    // Sans cela, « Une autre » pouvait rendre la même forme d'exercice avec
    // d'autres nombres — ce que fait déjà « changer les valeurs ». On vise donc
    // une forme que la liste ne porte pas encore, tant qu'il en reste une.
    if (!generatorKey && !blueprintId && !graine) {   // pas de reproduction en cours
      const forme = formeInedite();
      if (forme) {
        generatorKey = forme.generatorKey;
        blueprintId = forme.blueprintId || undefined;
      }
    }
    try {
      const r = await axios.post(`${BACKEND_URL}/api/v1/exercises/generate/batch`, {
        code_officiel: chapitre,
        batch_size: 1,
        difficulte,
        seed: Number.isFinite(graine) ? graine : Math.floor(Math.random() * 1000000),
        offer: isPro ? "pro" : "free",
        // LE NIVEAU EST DIT, PAS DEVINÉ.
        // Le serveur sait le retrouver depuis le chapitre, mais ce chemin
        // envoie aussi un générateur : la classe affichée à l'écran est
        // l'intention de l'utilisateur, elle doit voyager avec la demande.
        grade: niveau,
        ...(generatorKey ? { generator_key: generatorKey } : {}),
        ...(blueprintId ? { blueprint_id: blueprintId } : {}),
      });
      const neuf = (r.data?.exercises || [])[0];
      if (!neuf) {
        toast({ title: "Aucune autre proposition disponible", variant: "destructive" });
        return;
      }
      setProposals((liste) => {
        if (remplaceUid) {
          return liste.map((e) => (e.id_exercice === remplaceUid ? neuf : e));
        }
        return [...liste, neuf];
      });
      if (!remplaceUid) {
        setAnnonce("Une proposition ajoutée");
        montrerLesPropositions();
      }
    } catch (e) {
      toast({ title: "La proposition n'a pas pu être créée", description: "Réessayez dans un instant.", variant: "destructive" });
    } finally {
      setRemplaceId(null);
    }
  };

  const handleGenerate = async () => {
    if (!chapitre || generating) return;
    setGenerating(true);
    setGenError(null);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/v1/exercises/generate/batch`, {
        code_officiel: chapitre,
        batch_size: batchSize,
        difficulte,
        seed: Date.now() % 1000000,
        offer: isPro ? "pro" : "free",
        grade: niveau,
      });
      const exs = r.data?.exercises || [];
      setProposals(exs);
      setOpenSolutions({});
      setDepliees({});
      if (exs.length > 0) {
        setAnnonce(`${exs.length} proposition${exs.length > 1 ? "s" : ""} prête${exs.length > 1 ? "s" : ""}`);
        montrerLesPropositions();
      }
      if (exs.length === 0) {
        setGenError("Aucun exercice n'a pu être généré pour ce chapitre. Essayez un autre chapitre.");
      } else if (exs.length < batchSize) {
        toast({ title: `${exs.length} exercice(s) générés`, description: "Moins que demandé pour garantir des exercices variés et corrects." });
      }
    } catch (e) {
      setGenError("La génération a échoué. Réessayez dans un instant.");
    } finally {
      setGenerating(false);
    }
  };

  /**
   * Ajoute d'un coup les propositions qui ne sont pas déjà sur la fiche.
   *
   * L'audit UX a chiffré le parcours : dix actions pour une fiche de cinq
   * exercices, dont CINQ pour accepter ce qu'on venait de demander. Le tri fin
   * reste possible — chaque exercice ajouté garde ses commandes de suppression
   * et de réordonnancement dans la fiche.
   */
  const addAllProposals = () => {
    proposals.forEach((ex) => {
      const uid = ex.id_exercice || `gen-${Date.now()}`;
      if (!isSelected(uid)) addExercise({ ...ex, uniqueId: uid });
    });
  };

  const addProposal = (ex) => {
    const uid = ex.id_exercice || `gen-${Date.now()}`;
    if (isSelected(uid)) {
      removeExercise(uid);
      return;
    }
    addExercise({ ...ex, uniqueId: uid });
    // Le bouton passera de toute façon à « Dans la fiche ». Mais sur
    // téléphone la fiche n'est pas visible : rien ne confirmait le geste
    // au moment où on le fait. « Ajouté ✓ » tient une seconde, puis cède
    // la place à l'état permanent.
    setAjoutRecent(uid);
    setTimeout(() => setAjoutRecent((u) => (u === uid ? null : u)), 1000);
  };

  /* ---------------------- fiche (droite) ---------------------- */
  const [sheetTitle, setSheetTitle] = useState(() => memoire?.titre || "");
  const [vue, setVue] = useState("sujet");
  //: `includeCorrection` a disparu. Le produit exporte désormais DEUX FICHIERS
  //: distincts — « PDF sujet » ou « PDF corrigé » — des deux points d'entrée,
  //: comme le faisait déjà la page d'édition. Un état global qui décidait du
  //: contenu du fichier n'a plus d'objet : le choix se fait au moment du clic.
  //: Les onglets Sujet / Corrigé, eux, restent : ils décident de ce qu'on
  //: REGARDE, pas de ce qu'on télécharge.
  const [pdfOptionsOpen, setPdfOptionsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  /* Le type de document et la mise en page.
   *
   * Ce sont des PRÉSÉLECTIONS, jamais des choix imposés : elles viennent des
   * préférences enregistrées dans « Mon compte › Personnalisation », et
   * l'utilisateur les change ici pour une fiche sans rien réenregistrer. Sans
   * préférence, on garde exactement le comportement d'avant — « exercices »
   * et la mise en page standard. */
  /* Les mêmes réglages que « Mes fiches › Éditer › Options », dans le même
   * objet et servis par le même composant. Deux interfaces pour un même geste
   * finissent toujours par diverger : il n'y en a plus qu'une. */
  const [reglages, setReglages] = useState(VALEURS_INITIALES);

  useEffect(() => {
    let mort = false;
    const jeton = localStorage.getItem("lemaitremot_session_token");
    if (!jeton) return undefined;
    const entete = { headers: { "X-Session-Token": jeton } };
    Promise.all([
      axios.get(`${BACKEND_URL}/api/mathalea/pro/config`, entete).catch(() => null),
      axios.get(`${BACKEND_URL}/api/user/sheets/options-export`, entete).catch(() => null),
    ])
      .then(([config, options]) => {
        if (mort) return;
        const c = (config && config.data) || {};
        const o = (options && options.data) || {};
        // Des PRÉSÉLECTIONS, jamais des choix imposés : elles viennent de
        // « Mon compte › Personnalisation » et s'appliquent tant que
        // l'utilisateur n'a rien changé pour cette fiche.
        //
        // LE STYLE, LUI, VIENT DU SERVEUR. Prendre `default_template` brut
        // était le bug : un compte redevenu gratuit garde une préférence Pro
        // enregistrée, l'atelier l'envoyait, et l'aperçu comme le comptage de
        // pages répondaient 403 — fenêtre d'options vide et muette. Le serveur
        // résout un style EFFECTIF (préférence si elle est autorisée, style
        // gratuit sinon), et les deux points d'entrée démarrent donc sur le
        // même état. Ne remets pas `c.default_template` ici.
        setReglages((r) => ({
          ...r,
          documentType: c.default_document_type || r.documentType,
          templateId: o.template_effectif || r.templateId,
        }));
      })
      .catch(() => {});           // pas connecté, ou pas de préférence : on garde les valeurs d'usine
    return () => { mort = true; };
  }, []);
  const [isSaving, setIsSaving] = useState(false);
  //: L'identifiant de la fiche déjà enregistrée, et l'empreinte de ce qui a
  //: été envoyé. Relus au montage : sans cela, l'indicateur repartirait à
  //: « jamais enregistrée » après chaque rechargement, ce qui serait faux.
  const [savedSheetUid, setSavedSheetUid] = useState(() => memoire?.ficheUid || null);
  const [empreinteEnregistree, setEmpreinteEnregistree] = useState(
    () => memoire?.empreinte || null);
  /* LA RÉVISION QU'ON A RÉELLEMENT VUE.
   *
   * Elle était RELUE juste avant d'écrire — ce qui la rendait toujours
   * exacte, donc parfaitement inutile : le contrôle ne pouvait plus rien
   * refuser. Une sélection locale vieille d'une heure repartait avec une
   * révision fraîche et écrasait le travail fait ailleurs entre-temps.
   *
   * Celle-ci vient du dernier enregistrement RÉUSSI, et ne bouge qu'avec lui.
   * Si la fiche a changé ailleurs, le serveur refuse (409) et on le dit. */
  const [revisionEnregistree, setRevisionEnregistree] = useState(
    () => memoire?.revision ?? null);

  /* « Créer une fiche » ne doit ni rouvrir l'ancienne en silence, ni la jeter.
   *
   * Mes fiches annonce son intention par `?nouvelle=1`. Si une fiche est en
   * cours, on demande — vider sans demander perdrait un travail non
   * enregistré, et reprendre sans le dire est le défaut qu'on corrige. */
  const [choixNouvelleFiche, setChoixNouvelleFiche] = useState(false);
  const [demandeVidage, setDemandeVidage] = useState(false);

  /* La persistance vit ICI, après les états qu'elle lit.
   *
   * Placée plus haut, son tableau de dépendances — évalué à chaque rendu, et
   * non à l'exécution de l'effet — touchait `savedSheetUid` avant sa
   * déclaration : zone morte temporelle, et l'écran entier tombait sur
   * l'ErrorBoundary. Un `const` se hisse, mais pas son initialisation.
   *
   * On n'écrit qu'une fois la reprise tentée : sinon le premier rendu, où le
   * chapitre est encore vide, effacerait la mémoire qu'on s'apprête à lire. */
  useEffect(() => {
    if (!repriseFaite.current) return;
    try {
      localStorage.setItem(CLE_ATELIER, JSON.stringify({
        niveau, chapitre, titre: sheetTitle,
        ficheUid: savedSheetUid, empreinte: empreinteEnregistree,
        revision: revisionEnregistree,
      }));
    } catch (e) { /* pas de mémoire disponible : l'écran marche sans */ }
  }, [niveau, chapitre, sheetTitle, savedSheetUid, empreinteEnregistree,
      revisionEnregistree]);
  const [rerollingId, setRerollingId] = useState(null);
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);

  /* Le titre par défaut ne nomme un chapitre que si la fiche N'EN CONTIENT QU'UN.
   *
   * La sélection vit dans un contexte persisté : elle survit aux changements de
   * chapitre, aux rechargements et aux sessions. Le titre, lui, était calculé
   * à partir du chapitre ouvert au moment de l'export. Une fiche composée en
   * parcourant additions, durées et repérage, exportée depuis le chapitre
   * Fractions, s'appelait donc « Fractions simples — CE1 » alors que six de ses
   * sept exercices n'avaient rien à voir avec les fractions.
   *
   * Le titre n'était pas faux par accident : il ne regardait pas ce qu'il
   * nommait. */
  const chapitresDeLaSelection = useMemo(
    () =>
      selectedExercises.map((ex) =>
        (ex.chapitre || ex.metadata?.chapitre || "").toUpperCase()
      ),
    [selectedExercises]
  );

  /* Trois cas, et le troisième est celui qui a menti :
   * - fiche vide : rien ne contredit le chapitre ouvert, on le nomme ;
   * - tous les exercices en viennent : on le nomme ;
   * - un seul exercice vient d'ailleurs, ou n'annonce aucun chapitre : on ne
   *   nomme plus rien. Un exercice sans chapitre connu compte comme un
   *   exercice d'ailleurs — mieux vaut un titre générique qu'un titre faux. */
  const selectionTientDansLeChapitre =
    Boolean(currentChapter) &&
    (selectedExercises.length === 0 ||
      chapitresDeLaSelection.every(
        (code) => code && code === String(currentChapter.code_officiel).toUpperCase()
      ));

  /* Les niveaux réellement présents sur la fiche.
   *
   * Garder des exercices en changeant de classe est VOULU : c'est ainsi qu'on
   * compose une fiche différenciée, et l'audit UX a jugé cette conservation
   * souhaitable. Ce qui mentait, c'était le titre : il suivait le sélecteur de
   * classe et annonçait « — CE1 » sur une fiche contenant des fractions de 6e.
   * Une fiche multi-niveaux s'assume désormais et le dit. */
  const niveauxDeLaSelection = useMemo(() => {
    const vus = selectedExercises
      .map((ex) => (ex.niveau || ex.metadata?.niveau || "").trim())
      .filter(Boolean);
    return Array.from(new Set(vus));
  }, [selectedExercises]);

  const ficheDifferenciee = niveauxDeLaSelection.length > 1;

  const defaultTitle = ficheDifferenciee
    ? `Fiche différenciée — ${niveauxDeLaSelection.join(", ")}`
    : selectionTientDansLeChapitre
      ? `${currentChapter.libelle.split(" - ")[0]} — ${niveau}`
      : `Fiche d'exercices${niveau ? ` — ${niveau}` : ""}`;

  // Volet mobile : bloquer le scroll d'arrière-plan (pas de double scroll)
  useEffect(() => {
    if (mobileSheetOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
    return undefined;
  }, [mobileSheetOpen]);

  /* ------------------------- quota réel ------------------------- */
  const [quota, setQuota] = useState(null);
  const refreshQuota = useCallback(() => {
    if (!sessionToken) { setQuota(null); return; }
    axios.get(`${BACKEND_URL}/api/user/sheets/export-quota/status`, {
      headers: { "X-Session-Token": sessionToken },
    }).then((r) => setQuota(r.data)).catch(() => setQuota(null));
  }, [sessionToken]);
  useEffect(() => { refreshQuota(); }, [refreshQuota]);

  const quotaLine = (() => {
    if (authLoading) return "";
    if (!userEmail) return "Gratuit : 3 PDF par jour avec un compte gratuit — proposé au premier téléchargement.";
    if (quota?.unlimited) return "Exports illimités (compte Pro).";
    if (quota) return `${quota.remaining} export${quota.remaining > 1 ? "s" : ""} PDF restant${quota.remaining > 1 ? "s" : ""} aujourd'hui (offre gratuite : ${quota.limit}/jour).`;
    return "";
  })();

  /* ---------------------- changer les valeurs ---------------------- */
  const handleReroll = async (ex) => {
    const m = ex.metadata || {};
    const genKey = m.generator_key;
    if (!genKey || rerollingId) return;
    setRerollingId(ex.uniqueId);
    try {
      const params = {};
      const preset = m.preset_used || m.variables?.preset_used || ex.generation_meta?.params_used?.preset;
      const bp = m.blueprint_id_used || m.variables?.blueprint_id_used || ex.generation_meta?.params_used?.blueprint_id;
      if (preset) params.preset = preset;
      if (bp) params.blueprint_id = bp;
      const avant = empreinteExercice(ex);
      const r = await axios.post(`${BACKEND_URL}/api/v1/exercises/reroll-data`, {
        generator_key: genKey,
        //: La métadonnée s'appelle `template_id_used`. On lisait `template_id`,
        //: qui n'existe pas : c'était donc toujours « default » qui partait.
        //: Sans effet observable aujourd'hui — le champ est nul partout — mais
        //: l'épingle fonctionnera le jour où le backend le renseignera.
        template_id: m.template_id_used || m.template_id || "default",
        seed: Date.now(),
        params,
        code_officiel: ex.chapitre || chapitre,
        difficulty: m.difficulty_effective || m.difficulty || difficulte,
      });
      /* CERTAINS EXERCICES N'ONT QU'UNE FORME.
       *
       * Mesuré sur 130 couples générateur × blueprint : six d'entre eux
       * rendent exactement le même exercice quel que soit le seed — un
       * vocabulaire, une figure à nommer. L'action réussissait alors sans rien
       * changer, et annonçait quand même « nouvelles valeurs ». Le défaut n'est
       * pas dans le générateur, qui a le droit de n'avoir qu'une forme : il est
       * dans la promesse.
       *
       * On compare donc avant d'annoncer, et on ne remplace rien s'il n'y a
       * rien à remplacer. */
      if (empreinteExercice(r.data) === avant) {
        toast({
          title: "Aucune autre variante disponible pour cet exercice",
          description: "Celui-ci n'a qu'une seule forme. Vous pouvez en ajouter un autre à la place.",
        });
        return;
      }
      replaceExercise(ex.uniqueId, r.data);
      toast({ title: "Valeurs changées", description: "Même exercice, nouvelles valeurs." });
    } catch (e) {
      toast({ title: "Impossible de changer les valeurs", description: "Réessayez dans un instant.", variant: "destructive" });
    } finally {
      setRerollingId(null);
    }
  };

  const rerollMenu = (ex, index) => (
    <button type="button"
      aria-label={`Changer les valeurs de l'exercice ${index + 1} (même type, autres nombres)`}
      title="Changer les valeurs (même type d'exercice)"
      disabled={rerollingId !== null}
      onClick={() => handleReroll(ex)}>
      {rerollingId === ex.uniqueId ? "…" : "⟳"}
    </button>
  );

  /**
   * Signaler un exercice fautif — là où on le voit.
   *
   * Le geste n'existait que dans la bibliothèque : sur la fiche enregistrée,
   * ou derrière le menu « … » d'une fiche. Or c'est ici qu'on remarque
   * qu'un exercice est faux, à l'instant où il s'affiche. Devoir aller le
   * retrouver ailleurs revenait à s'assurer que personne ne signale.
   *
   * Réservé aux comptes qui portent la capacité — la même règle qu'ailleurs,
   * et jamais `isAdmin`.
   */
  const signalerMenu = (ex, index) => (
    peutSignaler ? (
      <button type="button"
        aria-label={`Signaler un problème sur l'exercice ${index + 1}`}
        title="Signaler un problème sur cet exercice"
        onClick={() => setExerciceSignale(ex)}>
        ⚑
      </button>
    ) : null
  );

  /* ------------------------- export PDF ------------------------- */
  const doExport = useCallback(async (token, avecCorrige) => {
    if (selectedExercises.length === 0) return;
    setIsExporting(true);
    try {
      const exportData = {
        title: sheetTitle || defaultTitle,
        layout: "classic",
        document_type: reglages.documentType,
        due_date: reglages.dueDate || undefined,
        template_id: reglages.templateId,
        custom_title: reglages.customTitle || undefined,
        duration: reglages.duration || undefined,
        show_branding: reglages.showBranding,
        densite: reglages.densite,
        adaptation: reglages.adaptation,
        include_correction: avecCorrige,

        exercises: selectedExercises.map((ex, index) => ({
          order: index + 1,
          enonce_html: ex.enonce_html,
          solution_html: ex.solution_html,
          figure_svg_enonce: ex.figure_svg_enonce || ex.figure_svg_question || ex.figure_svg,
          figure_svg_solution: ex.figure_svg_solution || ex.figure_svg_correction,
          metadata: ex.metadata,
        })),
      };
      const response = await axios.post(`${BACKEND_URL}/api/v1/sheets/export-selection`, exportData, {
        headers: { "X-Session-Token": token, "Content-Type": "application/json" },
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      // Même règle que dans « Mes fiches » : le nom dit ce que contient
      // le fichier, sinon les deux PDF d'une même fiche sont indiscernables.
      const suffixe = avecCorrige ? "_corrige" : "_sujet";
      link.download = `${sheetTitle || defaultTitle}${suffixe}_${new Date().toISOString().split("T")[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast({
        title: "PDF téléchargé",
        description: avecCorrige ? "Le corrigé, réservé à l'enseignant." : "La fiche élève, prête à distribuer.",
      });
      refreshQuota();
    } catch (err) {
      if (handleExportError(err, null, { type: "export_pdf" })) return;
      if (err.response?.status === 429) {
        // Pendant la bêta, ce message s'adresse forcément à quelqu'un SANS
        // compte : un compte bêta a l'accès complet et ne rencontre pas ce
        // quota. L'invitation juste n'est donc pas « payez », c'est « créez un
        // compte, c'est offert ».
        toast({
          title: "Vos 3 fiches gratuites du jour sont créées",
          description: beta.active
            ? `Créez un compte : l'accès complet est offert pendant ${Math.round(beta.jours / 30)} mois.`
            : "Revenez demain, ou passez en Pro pour continuer maintenant.",
          variant: "destructive",
          action: (
            <Button onClick={() => navigate("/pricing")} variant="outline" size="sm" className="h-8">
              {beta.active ? "Rejoindre la bêta" : "Passer en Pro"}
            </Button>
          ),
        });
        refreshQuota();
      } else {
        toast({ title: "Le PDF n'a pas pu être créé", description: "Réessayez ; si le problème persiste, écrivez-nous.", variant: "destructive" });
      }
    } finally {
      setIsExporting(false);
    }
  }, [selectedExercises, sheetTitle, defaultTitle, reglages, toast, refreshQuota, handleExportError, navigate]);

  // Reprise de l'export après connexion, chemin sans navigation.
  useEffect(() => {
    const onPending = (event) => {
      if (event.detail?.type !== "export_pdf") return;
      const note = reprendreActionApresConnexion();
      if (!note || !note.startsWith("export_pdf")) return;
      const jeton = localStorage.getItem("lemaitremot_session_token");
      if (jeton) setTimeout(() => doExport(jeton, note.endsWith("corrige")), 500);
    };
    window.addEventListener("lmm:execute-pending-action", onPending);
    return () => window.removeEventListener("lmm:execute-pending-action", onPending);
  }, [doExport]);

  const handleExportPDF = (avecCorrige = false) => {
    if (selectedExercises.length === 0) {
      toast({ title: "Votre fiche est vide", description: "Générez des exercices puis « Ajouter »." });
      return;
    }
    /* L'intention est notée AVEC le fichier demandé.
     *
     * Même cause que pour l'enregistrement : `pendingAction` vit dans un état
     * React et ne survit pas au lien magique, qui remonte l'application. Et
     * même quand la reprise fonctionnait — par mot de passe — elle rappelait
     * `doExport(jeton)` sans second argument : c'était donc TOUJOURS le sujet
     * qui repartait, même après un clic sur « PDF corrigé ». Le fichier voulu
     * fait partie de l'intention. */
    if (!checkBeforeExport(() => doExport(sessionToken, avecCorrige))) {
      noterActionApresConnexion(`export_pdf:${avecCorrige ? "corrige" : "sujet"}`);
      return;
    }
    doExport(sessionToken, avecCorrige);
  };

  /* Les chargeurs du panneau partagé. Ils disent seulement OÙ chercher ; le
   * panneau ne connaît pas les routes. Une sélection n'est pas encore une
   * fiche, elle a donc les siennes — mais elles passent par le même moteur. */
  const chargerOptionsExport = useCallback(async () => {
    const r = await axios.get(`${BACKEND_URL}/api/user/sheets/options-export`, {
      headers: { "X-Session-Token": sessionToken },
    });
    return r.data;
  }, [sessionToken]);

  const corpsSelection = useCallback((v, options = {}) => ({
    title: sheetTitle || defaultTitle,
    niveau,
    layout: "classic",
    include_correction: !!v.corrige,
    document_type: v.documentType,
    due_date: v.dueDate || undefined,
    template_id: v.templateId,
    custom_title: v.customTitle || undefined,
    duration: v.duration || undefined,
    show_branding: v.showBranding,
    densite: v.densite,
    adaptation: v.adaptation,
    exercises: selectedExercises.map((ex, i) => ({
      order: i + 1,
      enonce_html: ex.enonce_html,
      solution_html: ex.solution_html,
      figure_svg_enonce: ex.figure_svg_enonce || ex.figure_svg_question || ex.figure_svg,
      figure_svg_solution: ex.figure_svg_solution || ex.figure_svg_correction,
      metadata: ex.metadata,
    })),
    ...options,
  }), [sheetTitle, defaultTitle, niveau, selectedExercises]);

  const chargerApercuSelection = useCallback(async (v) => {
    const r = await axios.post(
      `${BACKEND_URL}/api/v1/sheets/export-selection-preview`,
      corpsSelection(v), { headers: { "X-Session-Token": sessionToken } });
    return r.data?.html || "";
  }, [sessionToken, corpsSelection]);

  const chargerPagesSelection = useCallback(async (v) => {
    const r = await axios.post(
      `${BACKEND_URL}/api/v1/sheets/export-selection-pages`,
      corpsSelection(v, { densite: undefined }),
      { headers: { "X-Session-Token": sessionToken } });
    return r.data?.densites || null;
  }, [sessionToken, corpsSelection]);

  /* ------------------------- sauvegarde ------------------------- */
  /* LA SAUVEGARDE PREND LE JETON EN PARAMÈTRE, elle ne le lit pas dans l'état.
   *
   * Quand elle repart après une connexion, `useAuth` n'a pas encore vu la
   * nouvelle session : c'est le jeton fraîchement écrit dans `localStorage`
   * qu'il faut passer. Même découpage que `doExport`, pour la même raison. */
  const doSave = useCallback(async (token) => {
    setIsSaving(true);
    try {
      const exercisesWithOrder = selectedExercises.map((exercise, index) => ({
        exercise_uid: exercise.uniqueId,
        order: index + 1,
        generator_key: exercise.generator_key || exercise.metadata?.generator_key || null,
        code_officiel: exercise.code_officiel || exercise.metadata?.code_officiel || exercise.chapitre || "",
        difficulty: exercise.difficulty || exercise.metadata?.difficulte || "moyen",
        seed: exercise.seed || exercise.metadata?.seed || null,
        variables: exercise.variables || exercise.metadata?.variables || {},
        enonce_html: exercise.enonce_html || "",
        solution_html: exercise.solution_html || "",
        metadata: {
          niveau: exercise.niveau || exercise.metadata?.niveau || "",
          chapitre: exercise.chapitre || exercise.metadata?.chapitre || "",
          ...exercise.metadata,
        },
      }));
      const corps = {
        title: sheetTitle || defaultTitle,
        description: `Fiche composée avec ${selectedExercises.length} exercice(s)`,
        exercises: exercisesWithOrder,
      };

      /* ENREGISTRER DEUX FOIS NE DOIT PAS FAIRE DEUX FICHES.
       *
       * `create-from-selection` crée, toujours — la route répond 201. Tant que
       * l'identifiant de la fiche n'était pas relu, chaque clic empilait une
       * copie. Le compte de l'utilisateur en portait la trace : deux fiches
       * « Fonctions — 3e » à une heure et demie d'intervalle.
       *
       * Une fiche qui a déjà un identifiant est donc MISE À JOUR. Mais la route
       * PUT exige que chaque exercice existe déjà dans la bibliothèque, alors
       * que la création, elle, les y verse au passage : un exercice ajouté
       * après le premier enregistrement ferait échouer la mise à jour. On les
       * verse donc d'abord, avec la fonction qui sert déjà à cela. */
      /* METTRE À JOUR, OU ENREGISTRER UNE COPIE ?
       *
       * Un identifiant en mémoire SANS la révision correspondante — la
       * mémoire des navigateurs qui ont enregistré avant l'existence du champ
       * — ne dit pas quelle version on a vue. S'en servir pour écraser, c'est
       * le scénario qui perdait le travail fait ailleurs. On crée alors une
       * nouvelle fiche : rien d'existant n'est touché, le travail local est
       * conservé, et l'utilisateur repart avec un identifiant et une révision
       * qui vont ensemble. Une copie de trop se supprime ; une fiche effacée,
       * non. */
      const copiePrevue = copieInattendue({
        sheetUid: savedSheetUid, revisionConnue: revisionEnregistree });
      let uid = decisionDEnregistrement({
        sheetUid: savedSheetUid, revisionConnue: revisionEnregistree,
      }) === "mettre-a-jour" ? savedSheetUid : null;
      let reponse;
      if (uid) {
        await importSelectionToAccount(token).catch(() => {});
        /* ON ENVOIE LA RÉVISION QU'ON A VUE, PAS CELLE DU MOMENT.
         *
         * La relire juste avant d'écrire la rendait toujours exacte : le
         * contrôle ne refusait plus rien, et une sélection locale périmée
         * recevait une révision fraîche qui lui permettait d'écraser le
         * travail fait ailleurs. C'est celle du dernier enregistrement réussi
         * qui vaut quelque chose — et s'il n'y en a pas, on ne met pas à
         * jour du tout : voir `decisionDEnregistrement`. */
        reponse = await fetch(`${BACKEND_URL}/api/user/sheets/${uid}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", "X-Session-Token": token },
          body: JSON.stringify({ ...corps, revision: revisionEnregistree }),
        });
        /* La fiche a pu être supprimée depuis Mes fiches. On ne veut ni
         * échouer, ni écrire dans le vide : on retombe sur une création. */
        if (reponse.status === 404) {
          uid = null;
          setSavedSheetUid(null);
          setRevisionEnregistree(null);
          reponse = null;
        }
        /* Et elle a pu être MODIFIÉE ailleurs : on ne réessaie pas en force,
         * on le dit. Écraser en silence est exactement le défaut corrigé. */
        if (reponse && reponse.status === 409) {
          const conflit = await reponse.json().catch(() => ({}));
          toast({
            title: "Cette fiche a été modifiée ailleurs",
            description: "Rouvrez-la depuis « Mes fiches » avant d'enregistrer.",
            variant: "destructive",
          });
          void conflit;
          return;
        }
      }
      if (!uid) {
        reponse = await fetch(`${BACKEND_URL}/api/user/sheets/create-from-selection`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Session-Token": token },
          body: JSON.stringify(corps),
        });
      }
      const result = await reponse.json();
      if (!reponse.ok) throw new Error(result.detail || "save failed");
      setSavedSheetUid(uid || result.sheet_uid);
      // La révision RENDUE par le serveur : sans elle, l'enregistrement
      // suivant repartirait avec une révision périmée et se ferait refuser
      // pour un conflit qui n'existe pas.
      setRevisionEnregistree(result?.revision ?? ((revisionEnregistree ?? 0) + 1));
      setEmpreinteEnregistree(empreinteFiche(sheetTitle || defaultTitle, selectedExercises));
      toast({
        title: copiePrevue
          ? "Enregistrée comme une nouvelle fiche"
          : uid ? "Fiche mise à jour dans Mes fiches" : "Fiche enregistrée dans Mes fiches",
        description: copiePrevue
          ? "La version précédente n'a pas été modifiée : cet atelier ne savait plus de quelle version il partait."
          : undefined,
        action: (
          <Button onClick={() => navigate("/mes-fiches")} variant="outline" size="sm" className="h-8">
            Voir Mes fiches
          </Button>
        ),
      });
    } catch (e) {
      toast({ title: "Impossible d'enregistrer la fiche", description: "Réessayez dans un instant.", variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  }, [selectedExercises, sheetTitle, defaultTitle, toast, navigate,
      savedSheetUid, revisionEnregistree, importSelectionToAccount]);

  const handleSave = () => {
    if (selectedExercises.length === 0) {
      toast({ title: "Votre fiche est vide", description: "Ajoutez des exercices avant d'enregistrer." });
      return;
    }
    if (!sessionToken) {
      /* LE CLIC N'EST PLUS PERDU DANS LA CONNEXION.
       *
       * On passait `openLogin("/generer")`, une simple destination : l'intention
       * d'enregistrer n'était mémorisée nulle part. Mesuré sur un compte réel —
       * l'utilisateur cliquait Enregistrer, se connectait, revenait, et AUCUN
       * appel de sauvegarde ne partait. Rien ne le lui disait ; il devait
       * recliquer sans savoir qu'il le devait, au moment précis où il découvre
       * la fonction.
       *
       * `pendingAction` existe déjà et sert à l'export depuis son propre
       * correctif. On s'y branche plutôt que d'inventer un second mécanisme. */
      noterActionApresConnexion("save_sheet");
      openLogin({ returnTo: "/generer", pendingAction: { type: "save_sheet" } });
      return;
    }
    doSave(sessionToken);
  };

  /* Deux chemins de retour, donc deux reprises — mais UNE SEULE intention.
   *
   * Les deux effets ci-dessous peuvent s'armer pour la même connexion :
   * `onAuthSuccess` pose le jeton et diffuse `lmm:auth-changed` AVANT
   * `lmm:execute-pending-action`. L'effet de session partirait donc en premier,
   * puis l'évènement referait un envoi — deux fiches pour un clic, dans un
   * chantier dont le sujet est justement les doublons.
   *
   * L'intention rangée sert donc de jeton d'usage unique : celui qui la
   * consomme enregistre, l'autre ne trouve rien et se tait.
   *
   * Mot de passe : la modale réussit sans quitter la page, l'évènement suffit. */
  useEffect(() => {
    const onPending = (event) => {
      if (event.detail?.type !== "save_sheet") return;
      if (reprendreActionApresConnexion() !== "save_sheet") return;
      const jeton = localStorage.getItem("lemaitremot_session_token");
      if (jeton) setTimeout(() => doSave(jeton), 500);
    };
    window.addEventListener("lmm:execute-pending-action", onPending);
    return () => window.removeEventListener("lmm:execute-pending-action", onPending);
  }, [doSave]);

  /* Lien magique : l'application s'est remontée, l'évènement est passé pendant
   * qu'on n'existait pas. On relit l'intention rangée avant la connexion.
   *
   * On attend que la sélection soit revenue du stockage : la consommer sur une
   * fiche encore vide effacerait l'intention sans rien enregistrer, et le clic
   * serait perdu une seconde fois. */
  useEffect(() => {
    if (!sessionToken || selectedExercises.length === 0) return;
    const note = reprendreActionApresConnexion();
    if (note === "save_sheet") doSave(sessionToken);
    else if (note && note.startsWith("export_pdf")) {
      doExport(sessionToken, note.endsWith("corrige"));
    }
  }, [sessionToken, selectedExercises.length, doSave, doExport]);

  /* Trois états, et un seul est rassurant.
   *
   * On compare l'empreinte de ce qu'on voit à celle de ce qui a été envoyé.
   * « Changer les valeurs » remplace un exercice en place : l'identifiant ne
   * bouge pas, le contenu si — l'empreinte doit donc le voir, sans quoi
   * l'écran annoncerait « enregistrée » sur une fiche modifiée. */
  const etatEnregistrement = selectedExercises.length === 0
    ? null
    : !savedSheetUid
      ? "jamais"
      : empreinteFiche(sheetTitle || defaultTitle, selectedExercises) === empreinteEnregistree
        ? "a-jour"
        : "modifie";

  useEffect(() => {
    if (searchParams.get("nouvelle") !== "1") return;
    if (selectedExercises.length > 0) setChoixNouvelleFiche(true);
    // Le paramètre a joué son rôle : on le retire pour qu'un rechargement ne
    // repose pas la question.
    navigate("/generer", { replace: true });
  }, [searchParams, selectedExercises.length, navigate]);

  /* Repartir à neuf, VRAIMENT à neuf.
   *
   * L'identifiant de la fiche doit tomber avec le reste : conservé, le premier
   * enregistrement de la fiche neuve écraserait l'ancienne par une mise à
   * jour. C'est le garde-fou de ce lot. */
  const commencerFicheVide = () => {
    clearSelection();
    setSheetTitle("");
    setSavedSheetUid(null);
    setEmpreinteEnregistree(null);
    setProposals([]);
    setChoixNouvelleFiche(false);
    toast({ title: "Nouvelle fiche", description: "La précédente reste dans Mes fiches si vous l'aviez enregistrée." });
  };

  /* RETIRER UN EXERCICE ÉTAIT MUET ET SANS RETOUR.
   *
   * Un clic malencontreux sur la croix effaçait un exercice sans un mot, et
   * le récupérer imposait de régénérer — sans aucune garantie de retomber sur
   * le même : c'est un tirage. On rend donc le geste réversible pendant
   * quelques secondes, plutôt que de demander confirmation à chaque retrait,
   * ce qui alourdirait un geste courant.
   *
   * L'exercice et sa POSITION sont capturés avant le retrait : l'ordre de la
   * fiche est un choix pédagogique, le rendre en fin de liste ne serait pas
   * une annulation. */
  const retirerAvecFilet = (uid) => {
    const index = selectedExercises.findIndex((ex) => ex.uniqueId === uid);
    if (index < 0) return;
    const retire = selectedExercises[index];
    removeExercise(uid);
    toast({
      title: "Exercice retiré",
      description: `Position ${index + 1} — annulable quelques secondes.`,
      action: (
        <Button variant="outline" size="sm" className="h-8"
          onClick={() => restoreExercise(retire, index)}>
          Annuler
        </Button>
      ),
    });
  };

  /* Vider n'est pas commencer une fiche neuve.
   *
   * « Commencer une fiche vide » repart sans identifiant : c'est une AUTRE
   * fiche. Vider retire le contenu de CELLE-CI, qui garde son identité — si
   * elle est enregistrée, la remplir autrement puis enregistrer la met à jour,
   * ce qui est bien ce qu'on attend d'une fiche qu'on retravaille. La copie
   * enregistrée, elle, ne bouge pas tant qu'on n'enregistre pas : une fiche
   * vide ne s'enregistre pas. */
  const viderLaFiche = () => {
    clearSelection();
    setDemandeVidage(false);
    toast({ title: "Fiche vidée", description: "Les exercices ont été retirés." });
  };

  /* ========================= rendu ========================= */

  const selectCls = "h-11 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground focus:outline-none";

  const leftPanel = (
    <div className="flex min-w-0 flex-col gap-5">
      {/* --- 1 · Choisir --- */}
      <section aria-labelledby="atelier-choisir" className="rounded-xl border border-border bg-card p-5 shadow-sm">
        <h2 id="atelier-choisir" className="mb-3 text-[13px] font-bold uppercase tracking-wide text-foreground">1 · Choisir</h2>

        <div className="mb-4">
          <span className="mb-1.5 block text-sm font-semibold text-foreground">Classe</span>
          <div role="group" aria-label="Classe" className="flex flex-wrap gap-1.5">
            {levels.map((c) => (
              <button key={c} type="button" aria-pressed={niveau === c}
                onClick={() => { setNiveau(c); setChapitre(""); setProposals([]); }}
                className={`min-h-[38px] min-w-[46px] rounded-full border px-3 text-[13px] font-semibold transition-colors ${
                  niveau === c
                    ? "border-foreground bg-foreground text-background"
                    : "border-border bg-card text-muted-foreground hover:border-muted-foreground hover:text-foreground"
                }`}>
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <Label htmlFor="atelier-search" className="mb-1.5 block text-sm font-semibold">
            Que doit travailler l'élève ?
          </Label>
          <div ref={zoneRecherche}>
          <div className="relative">
            <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input id="atelier-search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Ex : fractions, symétrie, périmètre…" className="h-11 bg-card pl-9" />
          </div>
          {results && (
            <div className="mt-2 max-h-[50vh] overflow-y-auto overflow-x-hidden rounded-md border border-border">
              {results.length === 0 ? (
                <div className="px-3 py-2.5">
                  <p className="text-[13px] text-muted-foreground">
                    Rien en {niveau} pour « {query} ».
                  </p>
                  {ailleurs.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {ailleurs.map((c) => (
                        <li key={`${c.niveau}-${c.code_officiel}`}>
                          {/* Le libellé annonce l'action : le clic change de
                              niveau ET sélectionne le chapitre. Jamais en
                              silence. */}
                          <button type="button"
                            onClick={() => { setNiveau(c.niveau); setChapitre(c.code_officiel);
                                             setProposals([]); setQuery(""); }}
                            className="block w-full rounded-sm px-1 py-1 text-left text-[13px] text-primary hover:bg-accent">
                            Voir en {c.niveau} — {c.libelle}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : results.slice(0, 6).map((c) => (
                <button key={c.code_officiel} type="button"
                  onClick={() => { setChapitre(c.code_officiel); setQuery(""); }}
                  className="block w-full border-b border-border bg-card px-3 py-2 text-left text-[13px] last:border-b-0 hover:bg-accent">
                  <span className="font-medium text-foreground">{c.libelle}</span>
                  <span className="block text-xs text-muted-foreground">{c.domaine}</span>
                </button>
              ))}
            </div>
          )}
          </div>
        </div>

        <div className="mb-4">
          <Label htmlFor="atelier-chapitre" className="mb-1.5 block text-sm font-semibold">Chapitre</Label>
          {catalogError ? (
            <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-[13px] text-destructive">
              {catalogError}{" "}
              <button type="button" className="font-semibold underline" onClick={() => setNiveau((n) => n)}>Réessayer</button>
            </p>
          ) : (
            <select id="atelier-chapitre" className={selectCls} value={chapitre}
              disabled={catalogLoading}
              onChange={(e) => { setChapitre(e.target.value); setProposals([]); }}>
              <option value="">{catalogLoading ? "Chargement…" : "Choisir un chapitre"}</option>
              {catalog?.domains?.map((d) => (
                <optgroup key={d.name} label={d.name}>
                  {(d.chapters || []).map((c) => (
                    <option key={c.code_officiel} value={c.code_officiel}>{c.libelle}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          )}
        </div>

        {/* LA DIFFICULTÉ EST UN CHOIX PÉDAGOGIQUE, PAS UNE OPTION AVANCÉE.
         *
         * Elle était repliée derrière « Options avancées » : zéro occurrence
         * visible avant dépliage. Le réglage qui décide de ce que l'élève
         * reçoit était le seul qu'il fallait aller chercher, à côté du nombre
         * d'exercices qui, lui, s'affichait. Elle le rejoint.
         *
         * Le repli disparaît avec elle : il ne contenait rien d'autre. */}
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div>
            <Label htmlFor="atelier-nb" className="mb-1.5 block text-sm font-semibold">Nombre d'exercices</Label>
            <select id="atelier-nb" className={`${selectCls} w-24`} value={batchSize}
              onChange={(e) => setBatchSize(Number(e.target.value))}>
              {[3, 5, 8, 10].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <Label htmlFor="atelier-diff" className="mb-1.5 block text-sm font-semibold">Difficulté</Label>
            <select id="atelier-diff" className={`${selectCls} w-40`} value={difficulte}
              onChange={(e) => setDifficulte(e.target.value)}>
              <option value="facile">Facile</option>
              <option value="moyen">Moyen</option>
              <option value="difficile">Difficile</option>
            </select>
          </div>
        </div>

        <Button className="h-12 w-full text-[15px]" onClick={handleGenerate}
          disabled={!chapitre || generating}>
          {generating ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Génération…</>)
            : `Générer ${batchSize} exercices`}
        </Button>
        {!chapitre && !catalogLoading && (
          <p className="mt-2 text-xs text-muted-foreground">Choisissez d'abord un chapitre (ou tapez une notion ci-dessus).</p>
        )}
        {genError && (
          <p role="alert" className="mt-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-[13px] text-destructive">{genError}</p>
        )}

        {/* CE QUE LE CHAPITRE CONTIENT VRAIMENT.
            Une ligne suffit à répondre à « est-ce que j'ai fait le tour ? ».
            Le détail ne s'ouvre que si on le demande : on ne transforme pas
            l'atelier en tableau de bord. */}
        {varietes && varietes.formes_total > 0 && (
          <div className="mt-3 rounded-lg border border-border bg-muted/30 px-3 py-2">
            <div className="flex flex-wrap items-baseline gap-x-2 text-[13px]">
              <span className="text-foreground">
                Ce chapitre propose <strong>{varietes.formes_total} forme{varietes.formes_total > 1 ? "s" : ""} d'exercices</strong>
                {varietes.series_total > 1 ? `, réparties en ${varietes.series_total} séries` : ""}.
              </span>
              <button type="button"
                className="text-primary underline underline-offset-2"
                aria-expanded={varietesOuvertes}
                onClick={() => setVarietesOuvertes((v) => !v)}>
                {varietesOuvertes ? "Masquer le détail" : "Voir lesquelles"}
              </button>
            </div>
            {varietesOuvertes && (
              <div className="mt-2 space-y-2">
                {varietes.series.map((serie) => (
                  <div key={serie.generator_key}>
                    <div className="text-[12px] font-semibold text-muted-foreground">{serie.libelle}</div>
                    <ul className="mt-1 flex flex-wrap gap-1.5">
                      {serie.variantes.map((v) => (
                        <li key={serie.generator_key + v.id}>
                          <button type="button"
                            className="rounded-full border border-border bg-card px-2.5 py-1 text-[12px] text-foreground hover:border-primary/50 hover:bg-accent"
                            title="Proposer un exercice de cette forme"
                            disabled={generating}
                            onClick={() => proposerUn({ generatorKey: serie.generator_key, blueprintId: v.id })}>
                            {v.libelle}
                          </button>
                        </li>
                      ))}
                      {serie.variantes.length === 0 && (
                        <li>
                          <button type="button"
                            className="rounded-full border border-border bg-card px-2.5 py-1 text-[12px] text-foreground hover:border-primary/50 hover:bg-accent"
                            disabled={generating}
                            onClick={() => proposerUn({ generatorKey: serie.generator_key })}>
                            Proposer un exercice de cette série
                          </button>
                        </li>
                      )}
                    </ul>
                  </div>
                ))}
                <p className="pt-1 text-[12px] text-muted-foreground">
                  Cliquez une forme pour en ajouter une proposition, sans remplacer les autres.
                </p>
                {/* REVOIR UN EXERCICE QU'ON A AIMÉ.
                    Chaque proposition affiche sa graine ; c'est le nombre qui
                    la reproduit à l'identique. Sans ce champ, le nombre était
                    une curiosité ; avec lui, il devient un signet. */}
                <div className="flex flex-wrap items-center gap-2 border-t border-border pt-2">
                  <label htmlFor="atelier-graine" className="text-[12px] text-muted-foreground">
                    Revoir une graine
                  </label>
                  <Input id="atelier-graine" value={graineVoulue} inputMode="numeric"
                    onChange={(e) => setGraineVoulue(e.target.value.replace(/[^0-9]/g, ""))}
                    placeholder="ex. 853199" className="h-8 w-28 text-[13px]" />
                  <Button size="sm" variant="outline" disabled={!graineVoulue || generating}
                    onClick={() => {
                      const memoire = origineDeLaGraine?.graine === graineVoulue
                        ? origineDeLaGraine : null;
                      proposerUn({
                        graine: Number(graineVoulue),
                        generatorKey: memoire?.generatorKey,
                        blueprintId: memoire?.blueprintId,
                      });
                    }}>
                    Reproduire
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* --- 2 · Ajouter --- */}
      {(generating || proposals.length > 0) && (
        <section ref={blocPropositions} aria-labelledby="atelier-ajouter" className="rounded-xl border border-border bg-card p-5 shadow-sm">
          <div className="mb-1 flex flex-wrap items-baseline gap-x-3">
            <h2 id="atelier-ajouter" className="text-[13px] font-bold uppercase tracking-wide text-foreground">2 · Garder ce qui convient</h2>
            {proposals.length > 0 && (
              <span className="text-[13px] font-medium text-primary">
                {proposals.length} proposition{proposals.length > 1 ? "s" : ""} prête{proposals.length > 1 ? "s" : ""}
              </span>
            )}
          </div>
          {/* Ce que l'écran montre, une oreille doit aussi l'apprendre. */}
          <p aria-live="polite" className="sr-only">{annonce}</p>
          <p className="mb-3 text-[13px] text-muted-foreground">« Ajouter » place l'exercice sur la fiche, à droite — tel qu'il sera imprimé.</p>

          {generating && proposals.length === 0 && (
            <div className="space-y-3" aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-24 animate-pulse rounded-lg bg-muted" />
              ))}
            </div>
          )}

          {proposals.length > 1 && (
            <div className="mb-3 flex items-center gap-2">
              <Button size="sm" onClick={addAllProposals}
                disabled={proposals.every((ex) => isSelected(ex.id_exercice))}>
                <Plus className="mr-1 h-4 w-4" />
                Tout ajouter à la fiche
              </Button>
              <span className="text-[13px] text-muted-foreground">
                ou choisissez un par un ci-dessous
              </span>
            </div>
          )}

          {proposals.map((ex, i) => {
            const uid = ex.id_exercice;
            const inSheet = isSelected(uid);
            const solOpen = !!openSolutions[uid];
            return (
              <article key={uid} className="mb-3 overflow-hidden rounded-lg border border-border last:mb-0">
                <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/50 px-3 py-2">
                  <span className="text-[13px] font-semibold text-foreground">
                    {/* variante → titre de l'exercice → rang. Dans cet ordre :
                        l'étiquette de variante est la plus précise quand elle
                        existe, le titre rendu vient ensuite, et le rang n'est
                        qu'un dernier recours. */}
                    {cleanLabel(ex.metadata?.blueprint_label)
                      || titreDeLExercice(ex.enonce_html)
                      || titreDeLaVariante(ex.metadata?.blueprint_id_used)
                      || `Proposition ${i + 1}`}
                  </span>
                  <span className="ml-auto" />
                  {/* VOIR AVANT D'AJOUTER.
                      Le seul moyen de juger un exercice était de l'ajouter, de
                      sortir le PDF et d'y chercher la page. */}
                  <Button size="sm" variant="ghost" className="text-muted-foreground"
                    onClick={() => setApercu(ex)} title="Voir l'exercice tel qu'il sera imprimé">
                    <Eye className="mr-1 h-4 w-4" />
                    Voir en grand
                  </Button>
                  {/* REMPLACER CELUI-LÀ, ET LUI SEUL.
                      Avant, changer une proposition obligeait à relancer tout
                      le lot — donc à perdre les quatre autres. */}
                  <Button size="sm" variant="ghost" className="text-muted-foreground"
                    disabled={remplaceId === uid || generating}
                    onClick={() => proposerUn({ remplaceUid: uid })}
                    title="Proposer un autre exercice à cette place">
                    <RefreshCw className={`mr-1 h-4 w-4 ${remplaceId === uid ? "animate-spin" : ""}`} />
                    Une autre
                  </Button>
                  <Button size="sm" variant={inSheet ? "secondary" : "outline"}
                    className={inSheet ? "text-primary" : "border-primary/40 text-primary hover:bg-accent"}
                    onClick={() => addProposal(ex)}
                    aria-pressed={inSheet}>
                    {ajoutRecent === uid
                      ? (<><Check className="mr-1 h-4 w-4" />Ajouté ✓</>)
                      : inSheet
                        ? (<><Check className="mr-1 h-4 w-4" />Dans la fiche</>)
                        : (<><Plus className="mr-1 h-4 w-4" />Ajouter</>)}
                  </Button>
                </div>
                {/* Pas de hauteur plafonnée ici. Cette section s'appelle
                    « garder ce qui convient » : tronquer à 224 px l'exercice
                    qu'on doit juger était contradictoire, et la barre de
                    défilement interne capturait celui de la page — quinze crans
                    de molette ne la bougeaient pas d'un pixel. */}
                <div className="px-3 py-2 text-sm">
                  <CorpsProposition deplie={!!depliees[uid]}
                    onDeplier={() => setDepliees((d) => ({ ...d, [uid]: !d[uid] }))}>
                    <MathHtmlRenderer html={ex.enonce_html} />
                    {(ex.figure_svg_enonce || ex.figure_svg) && (
                      <div className="my-1 max-w-full overflow-hidden [&_svg]:h-auto [&_svg]:max-w-full"
                        dangerouslySetInnerHTML={{ __html: ex.figure_svg_enonce || ex.figure_svg }} />
                    )}
                  </CorpsProposition>
                  {/* LA GRAINE, DITE.
                      « Retrouver ou reproduire un exercice grâce à sa graine »
                      n'était possible d'aucune façon : le nombre existait dans
                      le backend et ne sortait jamais. */}
                  {ex.metadata?.seed != null && (
                    <p className="mt-1.5 text-[11px] text-muted-foreground">
                      <button type="button"
                        className="font-mono underline underline-offset-2 hover:text-foreground"
                        title="Copier la graine — elle reproduit cet exercice à l'identique"
                        onClick={() => {
                          const g = String(ex.metadata.seed);
                          setGraineVoulue(g);
                          setOrigineDeLaGraine({
                            graine: g,
                            generatorKey: ex.metadata.generator_key,
                            blueprintId: ex.metadata.blueprint_id_used,
                          });
                          if (navigator.clipboard) navigator.clipboard.writeText(g).catch(() => {});
                          toast({ title: `Graine ${g} retenue`, description: "« Revoir une graine », dans le détail des formes, la reproduit." });
                        }}>
                        graine {ex.metadata.seed}
                      </button>
                      {ex.metadata?.generator_key ? ` · ${ex.metadata.generator_key}` : ""}
                    </p>
                  )}
                </div>
                <button type="button"
                  className="flex w-full items-center gap-1.5 border-t border-border bg-[hsl(142,60%,97%)] px-3 py-1.5 text-[13px] font-medium text-[hsl(142,60%,30%)] hover:bg-[hsl(142,60%,94%)]"
                  aria-expanded={solOpen}
                  onClick={() => setOpenSolutions((s) => ({ ...s, [uid]: !s[uid] }))}>
                  {solOpen ? "Masquer le corrigé" : "Voir le corrigé"}
                  {solOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                </button>
                {solOpen && (
                  <div className="border-t border-border bg-[hsl(142,60%,98%)] px-3 py-2 text-sm">
                    <MathHtmlRenderer html={ex.solution_html} />
                    {ex.figure_svg_solution && (
                      <div className="my-1 [&_svg]:h-auto [&_svg]:max-w-full"
                        dangerouslySetInnerHTML={{ __html: ex.figure_svg_solution }} />
                    )}
                  </div>
                )}
              </article>
            );
          })}

          {proposals.length > 0 && (
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {/* AJOUTER SANS EFFACER.
                  « Proposer d'autres exercices » remplaçait la liste entière :
                  une proposition qu'on hésitait à garder disparaissait au
                  premier clic. On peut désormais en demander une de plus. */}
              <Button variant="outline" size="sm" onClick={() => proposerUn({})} disabled={generating || !chapitre}>
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                Une proposition de plus
              </Button>
              <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={handleGenerate} disabled={generating}>
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                Recommencer un lot de {batchSize}
              </Button>
            </div>
          )}
        </section>
      )}
    </div>
  );

  const desk = (
    <div className="min-w-0">
      <div className="mb-4 flex flex-col gap-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <Input value={sheetTitle} onChange={(e) => setSheetTitle(e.target.value)}
            placeholder={defaultTitle}
            aria-label="Titre de la fiche"
            className="order-first h-10 w-full min-w-[160px] border-transparent bg-transparent px-1 font-serif text-xl font-bold sm:order-none sm:w-auto sm:min-w-0 sm:flex-1 text-foreground shadow-none placeholder:text-foreground/50 focus:border-input focus:bg-card" />
          <Button variant="outline" size="sm" className="h-10" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
            Enregistrer
          </Button>
          {/* Deux fichiers distincts, comme sur la page d'édition. */}
          <Button variant="outline" size="sm" className="h-10" onClick={() => handleExportPDF(false)} disabled={isExporting}>
            {isExporting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}
            PDF sujet
          </Button>
          <Button size="sm" className="h-10" onClick={() => handleExportPDF(true)} disabled={isExporting}>
            <Download className="mr-1.5 h-4 w-4" />
            PDF corrigé
          </Button>
        </div>
        {/* DIRE OÙ VIT CETTE FICHE.
            Rien à l'écran ne distinguait une fiche présente dans Mes fiches
            d'une fiche qui n'existe que dans ce navigateur. C'est la question
            que l'utilisateur se pose, et elle n'avait aucune réponse. */}
        {etatEnregistrement && (
          <p className={`text-xs ${etatEnregistrement === "a-jour"
            ? "text-muted-foreground" : "text-foreground/80"}`}
            aria-live="polite">
            {etatEnregistrement === "a-jour" && "Enregistrée dans Mes fiches."}
            {etatEnregistrement === "modifie" && "Modifications non enregistrées."}
            {etatEnregistrement === "jamais"
              && "Pas encore enregistrée — elle n'existe que dans ce navigateur."}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2.5">
          <div role="tablist" aria-label="Vue de la fiche" className="inline-flex rounded-full bg-muted p-0.5">
            {[["sujet", "Fiche élève"], ["corrige", "Corrigé"]].map(([k, label]) => (
              <button key={k} role="tab" aria-selected={vue === k} onClick={() => setVue(k)}
                className={`min-h-[44px] rounded-full px-4 text-[13px] font-semibold transition-colors ${
                  vue === k ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                }`}>
                {label}
              </button>
            ))}
          </div>
          <button type="button" onClick={() => setPdfOptionsOpen((v) => !v)} aria-expanded={pdfOptionsOpen}
            className="inline-flex min-h-[44px] items-center gap-1 px-1 text-[13px] text-muted-foreground hover:text-foreground">
            Options d'export <ChevronDown className="h-3.5 w-3.5" />
          </button>
          {/* « Vider la fiche » avait été retiré de l'écran et rien ne l'avait
              remplacé : repartir de zéro imposait de retirer les exercices un
              par un. Il revient, discret — c'est une action rare — et
              seulement quand il y a quelque chose à vider. */}
          {selectedExercises.length > 0 && (
            <button type="button" onClick={() => setDemandeVidage(true)}
              className="inline-flex min-h-[44px] items-center gap-1 px-1 text-[13px] text-muted-foreground hover:text-foreground">
              <X className="h-3.5 w-3.5" />Vider la fiche
            </button>
          )}
          {/* La case « Inclure le corrigé » a disparu : le choix se fait
              maintenant au moment du clic, entre deux fichiers. Les onglets
              ci-contre restent la VUE, ils ne décident de rien du fichier. */}
          <span className="ml-auto text-right text-xs text-muted-foreground">
            Deux fichiers séparés : sujet, ou corrigé.{" "}{quotaLine}
          </span>
        </div>
      </div>

      <div className="mx-auto max-w-[680px]">
        <SheetPreview
          title={sheetTitle || defaultTitle}
          exercises={selectedExercises}
          vue={vue}
          onMove={(from, to) => reorderExercises(from, to)}
          onRemove={retirerAvecFilet}
          rerollMenu={rerollMenu}
          signalerMenu={signalerMenu}
        />
      </div>
    </div>
  );

  // UNE seule instance, déclarée HORS de `desk`.
  //
  // `desk` est rendu deux fois — la colonne de bureau et le volet mobile —
  // et Radix portalise `DialogContent` vers <body>. Déclarer la fenêtre à
  // l'intérieur de `desk` montait donc DEUX portails visibles et superposés :
  // celui du dessus interceptait les clics destinés à l'autre, et chaque
  // instance lançait ses propres appels d'aperçu et de comptage de pages.
  // Le montage unique est la correction ; ne la remets pas dans `desk`.
  const fenetreOptions = (
    <Dialog open={pdfOptionsOpen} onOpenChange={setPdfOptionsOpen}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle>Options d'export PDF</DialogTitle>
          {/* Masquée sur téléphone : trois lignes de prose qui reformulent les
              trois questions posées juste en dessous — « Quel document ? »,
              « Combien de papier ? », « Un aménagement pour l'élève ? ».
              Elles coûtaient environ 120 px en tête d'une fenêtre où chaque
              pixel compte. Sur écran large, où la place ne manque pas, elles
              restent. */}
          <DialogDescription className="hidden lg:block">
            Le document que vous fabriquez, la place qu'il prend, et
            l'aménagement dont l'élève a besoin.
          </DialogDescription>
        </DialogHeader>
        <OptionsExportPDF
          valeurs={reglages}
          onChange={setReglages}
          chargerOptions={chargerOptionsExport}
          chargerApercu={chargerApercuSelection}
          chargerPages={chargerPagesSelection}
          isPro={isPro}
        />

        {/* PIED DE FENÊTRE COLLANT — téléphone uniquement.
            Mesuré : « Télécharger le PDF » et « Inclure le corrigé » vivent
            DERRIÈRE la modale, à y=117, couverts par elle. Le parcours
            Document → Densité → Aménagement → Aperçu se terminait donc par une
            fermeture de fenêtre pour aller chercher le bouton derrière.
            Ils reviennent ici, toujours atteignables sans défiler.

            Les deux commandes restent DISTINCTES et gardent leur sens :
            « Inclure le corrigé » décide du contenu du FICHIER exporté ; les
            onglets Sujet/Corrigé, plus haut, décident de ce qu'on REGARDE. On
            les rapproche visuellement, on ne les fusionne pas — regarder le
            corrigé en n'exportant que le sujet reste possible. */}
        <div className="sticky bottom-0 -mx-6 -mb-6 mt-2 border-t border-border bg-card px-4 pb-4 pt-3 lg:hidden">
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" className="h-12 text-[14px]"
              onClick={() => handleExportPDF(false)} disabled={isExporting}>
              {isExporting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}
              PDF sujet
            </Button>
            <Button className="h-12 text-[14px]" onClick={() => handleExportPDF(true)} disabled={isExporting}>
              <Download className="mr-1.5 h-4 w-4" />
              PDF corrigé
            </Button>
          </div>
          <p className="mt-1.5 text-center text-[11px] text-muted-foreground">
            Deux fichiers séparés. Regarder l'aperçu ne consomme rien.{" "}{quotaLine}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Le choix, quand on vient créer une fiche et qu'une autre est en cours.
          Aucune des deux issues n'est destructive par surprise : reprendre ne
          touche à rien, et commencer à neuf laisse la précédente dans Mes
          fiches si elle y avait été enregistrée — ce que le texte dit. */}
      <Dialog open={choixNouvelleFiche} onOpenChange={setChoixNouvelleFiche}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Vous avez une fiche en cours</DialogTitle>
            <DialogDescription>
              {selectedExercises.length} exercice{selectedExercises.length > 1 ? "s" : ""}
              {sheetTitle ? ` — « ${sheetTitle} »` : ""}.
              {etatEnregistrement === "a-jour"
                ? " Elle est enregistrée dans Mes fiches."
                : etatEnregistrement === "modifie"
                  ? " Elle a des modifications non enregistrées."
                  : " Elle n'est pas encore enregistrée."}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button variant="outline" className="h-11 flex-1"
              onClick={() => setChoixNouvelleFiche(false)}>
              Reprendre cette fiche
            </Button>
            <Button className="h-11 flex-1" onClick={commencerFicheVide}>
              Commencer une fiche vide
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Vider est irréversible : on demande, toujours, et on dit ce qui reste.
          Le texte distingue les deux cas, parce que l'inquiétude n'est pas la
          même selon que la fiche existe ailleurs ou non. */}
      <Dialog open={demandeVidage} onOpenChange={setDemandeVidage}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Vider la fiche ?</DialogTitle>
            <DialogDescription>
              {selectedExercises.length} exercice{selectedExercises.length > 1 ? "s" : ""} {selectedExercises.length > 1 ? "seront retirés" : "sera retiré"} de l'écran.
              {savedSheetUid
                ? " La version déjà dans Mes fiches n'est pas touchée : elle ne changera qu'au prochain enregistrement."
                : " Cette fiche n'est pas enregistrée : les exercices seront perdus."}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button variant="outline" className="h-11 flex-1"
              onClick={() => setDemandeVidage(false)}>
              Garder la fiche
            </Button>
            <Button variant="destructive" className="h-11 flex-1" onClick={viderLaFiche}>
              Vider la fiche
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="mx-auto grid max-w-[1280px] grid-cols-1 items-start gap-7 px-4 py-6 pb-28 lg:grid-cols-[370px_minmax(0,1fr)] lg:pb-14">
        <h1 className="sr-only">Créer une fiche d'exercices</h1>
        {leftPanel}
        {/* La fiche suit le regard sur écran large.

            Ce `sticky` n'a pu fonctionner qu'une fois `overflow-x: hidden`
            remplacé par `overflow: clip` sur html, body, #root et le conteneur
            de AppWithNav : `hidden` faisait de chacun un conteneur de
            défilement, et l'adhérence s'y accrochait au lieu du viewport —
            mesuré, la colonne partait à -619 px. `clip` rogne pareil sans cet
            effet de bord. Ne remets pas `hidden` sans lire index.css.

            `max-h` + `overflow-y-auto` : le défilement interne ne s'enclenche
            QUE si la fiche dépasse l'écran. Pas d'`overscroll-contain` : en
            bout de course, la molette doit rendre la main à la page. */}
        <div className="hidden lg:block lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto">
          {desk}
        </div>
      </div>

      {/* ----- Mobile : volet « Ma fiche » ----- */}
      <button type="button"
        onClick={() => setMobileSheetOpen(true)}
        className="fixed inset-x-3 bottom-3 z-40 flex items-center gap-2.5 rounded-xl bg-foreground px-4 py-3.5 text-[15px] font-semibold text-background shadow-lg lg:hidden">
        Ma fiche
        <span className="rounded-full bg-primary px-2.5 py-0.5 text-[13px] text-primary-foreground">
          {selectedExercises.length} exercice{selectedExercises.length > 1 ? "s" : ""}
        </span>
        <span className="ml-auto font-medium opacity-80">Aperçu &amp; PDF ↑</span>
      </button>

      {mobileSheetOpen && (
        <div role="dialog" aria-modal="true" aria-label="Ma fiche — aperçu et téléchargement"
          className="fixed inset-0 z-50 overflow-y-auto bg-background lg:hidden">
          <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-background/95 px-4 py-2.5 backdrop-blur-sm">
            <span className="font-serif text-lg font-bold text-foreground">Ma fiche</span>
            <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setMobileSheetOpen(false)} aria-label="Fermer l'aperçu">
              <X className="mr-1 h-4 w-4" />Fermer
            </Button>
          </div>
          <div className="px-3 py-4">{desk}</div>
        </div>
      )}

      {fenetreOptions}

      {/* Le signalement qui ne porte pas sur un exercice : la page s'affiche
          mal, ou ces options ne produisent rien. Il vit ici, au pied de la
          page, parce qu'il doit rester atteignable même quand aucun exercice
          n'a pu être produit — c'est précisément ce cas-là qu'on ne pouvait
          pas signaler. */}
      <div className="flex justify-center px-4 py-6">
        <SignalerLaPage
          contexte={{
            niveau,
            chapitre,
            difficulte,
            nombre_demande: batchSize,
            erreur_affichee: genError ? String(genError).slice(0, 300) : null,
          }}
        />
      </div>

      {/* Une seule instance, hors de `desk`, pour la même raison que la
          fenêtre d'options : `desk` est rendu deux fois et Radix portalise. */}
      <BugReportDialog
        open={Boolean(exerciceSignale)}
        onOpenChange={(ouvert) => { if (!ouvert) setExerciceSignale(null); }}
        exercise={exerciceSignale}
        chapterCode={exerciceSignale?.metadata?.chapter_code || chapitre || null}
      />

      {/* Une seule instance, comme les deux fenêtres ci-dessus : `leftPanel`
          est rendu dans la colonne ET dans le volet mobile. */}
      <ApercuExercice
        ouvert={Boolean(apercu)}
        exercice={apercu}
        titre={apercu ? (cleanLabel(apercu.metadata?.blueprint_label)
          || titreDeLExercice(apercu.enonce_html)
          || titreDeLaVariante(apercu.metadata?.blueprint_id_used) || "") : ""}
        dansLaFiche={apercu ? isSelected(apercu.id_exercice) : false}
        remplacementEnCours={Boolean(apercu && remplaceId === apercu.id_exercice)}
        onFermer={() => setApercu(null)}
        onAjouter={() => { if (apercu) { addProposal(apercu); } }}
        onRemplacer={() => { if (apercu) { proposerUn({ remplaceUid: apercu.id_exercice }); setApercu(null); } }}
      />
    </div>
  );
}
